import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// Color palette for different courses
const COLORS = [
  { border: 'rgb(75, 192, 192)', background: 'rgba(75, 192, 192, 0.2)' },
  { border: 'rgb(255, 99, 132)', background: 'rgba(255, 99, 132, 0.2)' },
  { border: 'rgb(54, 162, 235)', background: 'rgba(54, 162, 235, 0.2)' },
  { border: 'rgb(255, 206, 86)', background: 'rgba(255, 206, 86, 0.2)' },
  { border: 'rgb(153, 102, 255)', background: 'rgba(153, 102, 255, 0.2)' },
];

export const ActivityScoreChart = ({ activity, courses, scores, formatDate, t }) => {
  const chartData = useMemo(() => {
    if (!scores || scores.length === 0) return null;

    // Get labels (dates)
    const labels = scores.map(s => formatDate(s.date, 'short'));

    // Create datasets for each course
    const datasets = courses.map((course, index) => {
      const color = COLORS[index % COLORS.length];
      const data = scores.map(s => s[course.id]);

      return {
        label: course.name,
        data: data,
        borderColor: color.border,
        backgroundColor: color.background,
        tension: 0.3,
        spanGaps: true, // Connect lines across null values
        pointRadius: 4,
        pointHoverRadius: 6,
      };
    });

    return { labels, datasets };
  }, [scores, courses, formatDate]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        display: courses.length > 1, // Only show legend if multiple courses
      },
      title: {
        display: true,
        text: activity?.name || '',
        font: { size: 16 },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            if (value === null || value === undefined) {
              return `${label}: ${t('dashboard.index.children.activity_scores.no_data')}`;
            }
            return `${label}: ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: t('dashboard.index.children.activity_scores.date'),
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: t('dashboard.index.children.activity_scores.score'),
        },
        beginAtZero: false,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  }), [activity, courses, t]);

  if (!chartData) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-graph-up fs-1 d-block mb-3"></i>
        <p>{t('dashboard.index.children.activity_scores.no_data_available')}</p>
      </div>
    );
  }

  return (
    <div style={{ height: '300px' }} data-testid="activity-score-chart">
      <Line data={chartData} options={options} />
    </div>
  );
};
